import axios, { AxiosError, AxiosInstance } from 'axios'

// Type definition for Vite import.meta.env
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_URL?: string
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv
  }
}

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add token to requests if available
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    // Handle errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401 && !window.location.pathname.startsWith('/auth/callback')) {
          localStorage.removeItem('auth_token')
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    )
  }

  // Auth APIs
  async signUp(email: string, password: string, username: string) {
    return this.client.post('/api/auth/signup', { email, password, username })
  }

  async login(email: string, password: string) {
    return this.client.post('/api/auth/login', { email, password })
  }

  async logout() {
    return this.client.post('/api/auth/logout')
  }

  async getMe() {
    return this.client.get('/api/auth/me')
  }

  // Messaging APIs
  async getServers() {
    return this.client.get('/api/servers')
  }

  async createServer(name: string, description?: string) {
    return this.client.post('/api/servers', { name, description })
  }

  async getChannels(serverId: string) {
    return this.client.get(`/api/servers/${serverId}/channels`)
  }

  async createChannel(serverId: string, name: string, type: 'text' | 'voice' = 'text', isReadOnly: boolean = false) {
    return this.client.post(`/api/servers/${serverId}/channels`, { name, channel_type: type, is_read_only: isReadOnly })
  }

  async getServerMembers(serverId: string) {
    return this.client.get(`/api/servers/${serverId}/members`)
  }

  async getMessages(channelId: string, limit: number = 50) {
    return this.client.get(`/api/channels/${channelId}/messages?limit=${limit}`)
  }

  async sendMessage(channelId: string, content: string, replyToId?: string) {
    return this.client.post(`/api/channels/${channelId}/messages`, { content, reply_to_id: replyToId })
  }

  async editMessage(channelId: string, messageId: string, content: string) {
    return this.client.patch(`/api/channels/${channelId}/messages/${messageId}`, { content })
  }

  async deleteMessage(channelId: string, messageId: string) {
    return this.client.delete(`/api/channels/${channelId}/messages/${messageId}`)
  }

  async addReaction(channelId: string, messageId: string, emoji: string) {
    return this.client.post(`/api/channels/${channelId}/messages/${messageId}/reactions`, { emoji })
  }

  async removeReaction(channelId: string, messageId: string, emoji: string) {
    return this.client.delete(`/api/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`)
  }

  // Call APIs
  async initiateCall(recipientId: string, callType: 'voice' | 'video') {
    return this.client.post('/api/calls', { recipientId, callType })
  }

  async endCall(callId: string) {
    return this.client.post(`/api/calls/${callId}/end`)
  }

  // User APIs
  async getUser(userId: string) {
    return this.client.get(`/api/users/${userId}`)
  }

  async updateProfile(data: any) {
    return this.client.patch('/api/users/profile', data)
  }

  // Relationships APIs
  async sendFriendRequest(targetUserId: string) {
    return this.client.post('/api/relationships/friend-request', { targetUserId })
  }

  async getFriendRequests() {
    return this.client.get('/api/relationships/friend-requests')
  }

  async acceptFriendRequest(relationshipId: string) {
    return this.client.post(`/api/relationships/${relationshipId}/accept`)
  }

  async rejectFriendRequest(relationshipId: string) {
    return this.client.post(`/api/relationships/${relationshipId}/reject`)
  }

  // Premium APIs
  async getPremiumStatus() {
    return this.client.get('/api/premium/status')
  }

  async upgradePremium(tier: 'premium' | 'enterprise') {
    return this.client.post('/api/premium/upgrade', { tier })
  }

  // Notification APIs
  async getNotifications() {
    return this.client.get('/api/notifications')
  }

  async markNotificationAsRead(notificationId: string) {
    return this.client.patch(`/api/notifications/${notificationId}/read`)
  }

  async markAllNotificationsAsRead() {
    return this.client.post('/api/notifications/mark-all-read')
  }

  async clearAllNotifications() {
    return this.client.delete('/api/notifications/clear-all')
  }

  // Search APIs
  async searchUsers(query: string) {
    return this.client.get(`/api/search/users?q=${encodeURIComponent(query)}`)
  }

  async searchServers(query: string) {
    return this.client.get(`/api/search/servers?q=${encodeURIComponent(query)}`)
  }

  async searchMessages(query: string) {
    return this.client.get(`/api/search/messages?q=${encodeURIComponent(query)}`)
  }

  // Admin APIs
  async listAllUsers() {
    return this.client.get('/api/auth/admin/users')
  }

  async listAllServers() {
    return this.client.get('/api/admin/servers')
  }

  async deleteServer(serverId: string) {
    return this.client.delete(`/api/admin/servers/${serverId}`)
  }

  async banUser(userId: string) {
    return this.client.post(`/api/admin/users/${userId}/ban`)
  }

  async unbanUser(userId: string) {
    return this.client.post(`/api/admin/users/${userId}/unban`)
  }

  // Friends/Blocking APIs
  async getFriends() {
    return this.client.get('/api/relationships/friends')
  }

  async getBlockedUsers() {
    return this.client.get('/api/relationships/blocked')
  }

  async blockUser(userId: string) {
    return this.client.post('/api/relationships/block', { userId })
  }

  async unblockUser(userId: string) {
    return this.client.post('/api/relationships/unblock', { userId })
  }

  async removeFriend(userId: string) {
    return this.client.delete(`/api/relationships/friends/${userId}`)
  }
}

export const apiClient = new ApiClient()

// Service exports for easy importing
export const authService = {
  signup: (email: string, password: string, username: string) =>
    apiClient.signUp(email, password, username),
  login: (email: string, password: string) => apiClient.login(email, password),
  logout: () => apiClient.logout(),
  getMe: () => apiClient.getMe(),
}

export const serverService = {
  list: () => apiClient.getServers(),
  get: (id: string) => apiClient['client'].get(`/api/servers/${id}`),
  channels: (id: string) => apiClient.getChannels(id),
  members: (id: string) => apiClient.getServerMembers(id),
  create: (name: string, description?: string) =>
    apiClient.createServer(name, description),
  createChannel: (serverId: string, name: string, type: 'text' | 'voice', isReadOnly: boolean = false) =>
    apiClient.createChannel(serverId, name, type, isReadOnly),
  join: (inviteCode: string) =>
    apiClient['client'].post(`/api/servers/join/${inviteCode.toUpperCase()}`),
  delete: (id: string) => apiClient.deleteServer(id),
  search: (query: string) => apiClient.searchServers(query),
  kickMember: (serverId: string, userId: string) =>
    apiClient['client'].post(`/api/servers/${serverId}/members/${userId}/kick`),
  banMember: (serverId: string, userId: string, reason?: string) =>
    apiClient['client'].post(`/api/servers/${serverId}/members/${userId}/ban`, { reason }),
  unbanMember: (serverId: string, userId: string) =>
    apiClient['client'].post(`/api/servers/${serverId}/members/${userId}/unban`),
}

export const messageService = {
  list: (channelId: string, limit?: number) => apiClient.getMessages(channelId, limit),
  send: (channelId: string, content: string, replyToId?: string) =>
    apiClient.sendMessage(channelId, content, replyToId),
  edit: (channelId: string, messageId: string, content: string) =>
    apiClient.editMessage(channelId, messageId, content),
  remove: (channelId: string, messageId: string) =>
    apiClient.deleteMessage(channelId, messageId),
  addReaction: (channelId: string, messageId: string, emoji: string) =>
    apiClient.addReaction(channelId, messageId, emoji),
  removeReaction: (channelId: string, messageId: string, emoji: string) =>
    apiClient.removeReaction(channelId, messageId, emoji),
  search: (query: string) => apiClient.searchMessages(query),
}

export const callService = {
  initiate: (recipientId: string, callType: 'voice' | 'video') =>
    apiClient.initiateCall(recipientId, callType),
  accept: (callId: string) => apiClient['client'].post(`/api/calls/${callId}/accept`),
  decline: (callId: string) => apiClient['client'].post(`/api/calls/${callId}/decline`),
  end: (callId: string) => apiClient.endCall(callId),
}

export const userService = {
  get: (userId: string) => apiClient.getUser(userId),
  list: () => apiClient.listAllUsers(),
  updateProfile: (_userId: string, data: any) => apiClient.updateProfile(data),
  search: (query: string) => apiClient.searchUsers(query),
  getFriends: () => apiClient.getFriends(),
  getFriendRequests: () => apiClient.getFriendRequests(),
  sendFriendRequest: (userId: string) => apiClient.sendFriendRequest(userId),
  acceptFriendRequest: (requestId: string) => apiClient.acceptFriendRequest(requestId),
  declineFriendRequest: (requestId: string) =>
    apiClient.rejectFriendRequest(requestId),
  removeFriend: (userId: string) => apiClient.removeFriend(userId),
  getBlockedUsers: () => apiClient.getBlockedUsers(),
  blockUser: (userId: string) => apiClient.blockUser(userId),
  unblockUser: (userId: string) => apiClient.unblockUser(userId),
  ban: (userId: string) => apiClient.banUser(userId),
  unban: (userId: string) => apiClient.unbanUser(userId),
}

export const notificationService = {
  list: () => apiClient.getNotifications(),
  markAsRead: (notificationId: string) =>
    apiClient.markNotificationAsRead(notificationId),
  markAllAsRead: () => apiClient.markAllNotificationsAsRead(),
  clearAll: () => apiClient.clearAllNotifications(),
}
